import React from 'react';
import { useTranslation } from 'react-i18next';

import { ModerationHistory } from '../../../../../../models';
import useDateFormat from '../../../../../../components/utils/useDateFormat';
import { colors, formatMailStatus, getMailStatus, getMailStatusPriority } from '../../../../../../constants/moderation';

import { Card, ContentCard, EmptyState, HeaderCard, Icon, SubCard, SubTitleCard, TitleCard, Status, Button, ToastContext, StandardHeader } from '@weezevent/nacre';
import { SideBar } from '@weezevent/socium';

import Style from '../../moderationProfile.module.css';
import { translatableKey } from '../../../../utils';

export default function ModerationProfileHistory({ organizationId, flowId, attendee, historyDatas, loadingHistoryDatas, syncHistory }) {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);

    const handleReapply = React.useCallback(
        (id, action) => {
            ModerationHistory.trigger({
                organizationId,
                flowId,
                id,
                actionId: action
            })
                .then(() => {
                    toast.success(t('ticket.moderation.attendee.history.action.success'));
                    syncHistory();
                })
                .catch(() => {
                    toast.error(t('ticket.moderation.attendee.history.action.error'));
                });
        },
        [organizationId, flowId, syncHistory]
    );

    return (
        <Card id="history" className={`${Style['attendee-card']} ${Style['attendee-history']}`}>
            <HeaderCard>
                <TitleCard title={t('ticket.moderation.attendee.history.title')} />
                <SubTitleCard title={t('ticket.moderation.attendee.history.subtitle')} />
            </HeaderCard>
            <ContentCard>
                {!historyDatas?.length && !loadingHistoryDatas ? (
                    <EmptyState icon={<Icon margin name="history" />} title={t('ticket.moderation.attendee.history.emptyStateTitle')} />
                ) : (
                    historyDatas
                        ?.sort((a, b) => Date.parse(b.created) - Date.parse(a.created))
                        .map(historyData => (
                            <SubCardHistory datas={historyDatas} key={historyData.id} historyData={historyData} attendee={attendee} handleReapply={handleReapply} />
                        ))
                )}
            </ContentCard>
        </Card>
    );
}

const SubCardHistory = ({ datas, historyData, handleReapply, attendee }) => {
    const { t } = useTranslation();
    const [, formattedTime, formattedDate] = useDateFormat({
        date: historyData.created,
        tz: attendee.event_timezone,
        dateStyle: 'short',
        timeStyle: 'short'
    });

    const historyText = React.useMemo(() => {
        return historyData.text
            .replaceAll('True', 'checked')
            .replaceAll('False', 'not checked')
            .split('\n');
    }, [historyData]);

    const formatLogText = value => {
        // Differentiate action messages from basic transition statuses
        if (!value.includes('->')) {
            return t(`ticket.moderation.attendee.history.action.${translatableKey(value)}`);
        }
        return value;
    };

    const formatLogValue = React.useCallback(
        value => {
            // Ensure a clear pattern and match on form update action logs
            const action = historyText[0].toLowerCase().replaceAll(' ', '_');

            if (!action.includes('form_updated')) return value;

            const formKey = value.split(':')[0];
            const formLabel = attendee.form.find(it => it.weez_id === formKey || it.source === formKey)?.question;
            return formKey && formLabel ? value.replace(formKey, formLabel) : value;
        },
        [historyText, attendee.form]
    );

    return (
        <SubCard
            className={Style['attendee-history-subCard']}
            title={
                <div className={Style['attendee-history-subCardTitle']}>
                    <b>
                        {formattedDate} - {formattedTime}
                    </b>
                    <span>{historyData.user?.full_name}</span>
                </div>
            }
        >
            <Status color={colors[historyData.destination_status.stage]} text={historyData.destination_status.name} />
            <div className={Style['attendee-history-actionName']}>
                {historyText.map((it, index) => {
                    if (index === 0) {
                        return <p key={index}>{formatLogText(it)}</p>;
                    }
                    return <li key={index}>{formatLogValue(it)}</li>;
                })}
            </div>
            <div className={Style['attendee-history-actionName']}>
                {historyData.action_logs.map(action => (
                    <ActionCardHistory key={action.id} datas={datas} id={historyData.pk} action={action} handleReapply={handleReapply} />
                ))}
            </div>
        </SubCard>
    );
};

const ActionCardHistory = ({ datas, id, action, handleReapply }) => {
    const { t } = useTranslation();
    const [isEmailStatusHistoryOpen, setEmailStatusHistoryOpen] = React.useState(false);

    const isLatestAction = React.useMemo(() => {
        const actions = datas?.reduce((acc, act) => {
            return acc.concat(act.action_logs.filter(ac => ac.action.type === 'email_action').map(ac => ac));
        }, []);

        if (!actions.length) {
            return false;
        }

        return actions[0].id === action.id;
    }, [datas, action]);

    const isEmailAction = React.useMemo(() => {
        return action.action.type === 'email_action';
    }, [action]);

    const sortedActionDetails = React.useMemo(() => {
        return [...action.details].sort((a, b) => new Date(b.action_date) - new Date(a.action_date));
    }, [action.details]);

    const actionText = React.useMemo(() => {
        const text = action.text.trim().split(':');
        const success = action.is_success;

        const key = t(`ticket.moderation.attendee.history.action.${translatableKey(text[0])}`);
        const email = text[1];
        const commentKey = text[2] && success ? t(`ticket.moderation.attendee.history.action.${translatableKey(text[2])}`) : '';
        const comment = text[3] ?? '';

        return `${key} ${email} ${commentKey} ${comment}`;
    }, [action]);

    return (
        <div className={Style['log-action-block']}>
            <span>
                <b>{t('ticket.moderation.attendee.history.action.text')} : </b>
                {actionText}
            </span>
            {isEmailAction && (
                <>
                    {!!action.details.length > 0 && <EmailTransitionActionStatus sortedActionDetails={sortedActionDetails} />}
                    <Button.Group className={Style['action-buttons']}>
                        {isLatestAction && action.action.allow_replay && (
                            <Button
                                primary
                                onlyIconMobile={false}
                                icon={<Icon name="mail-send" />}
                                label={t('ticket.moderation.attendee.history.resend')}
                                onClick={() => handleReapply(id, action.action.id)}
                            />
                        )}
                        {!!action.details?.length > 0 && (
                            <Button
                                primary
                                inverted
                                onlyIconMobile={false}
                                icon={<Icon name="history" />}
                                label={t('ticket.moderation.attendee.history.emailStatus.cta')}
                                onClick={() => setEmailStatusHistoryOpen(true)}
                            />
                        )}
                    </Button.Group>
                    {!!action.details?.length > 0 && (
                        <SideBar open={isEmailStatusHistoryOpen} onClose={() => setEmailStatusHistoryOpen(false)} onClickAway={() => setEmailStatusHistoryOpen(false)}>
                            <StandardHeader title={t('ticket.moderation.attendee.history.emailStatus.cta')} />
                            {sortedActionDetails.map((action, index) => (
                                <Card key={index} className={Style['email-status-card']}>
                                    <ContentCard>
                                        <Status
                                            className={Style['email-status-item']}
                                            color={getMailStatus(action.event)}
                                            text={t(
                                                [
                                                    `ticket.moderation.attendee.history.emailStatus.${formatMailStatus(action.event)}`,
                                                    'ticket.moderation.attendee.history.emailStatus.fallback'
                                                ],
                                                {
                                                    action: action.event
                                                }
                                            )}
                                        />
                                        <div>{new Date(action.action_date).toLocaleString()}</div>
                                    </ContentCard>
                                </Card>
                            ))}
                        </SideBar>
                    )}
                </>
            )}
        </div>
    );
};

const EmailTransitionActionStatus = ({ sortedActionDetails }) => {
    const { t } = useTranslation();

    const highestPriorityStatus = React.useMemo(() => {
        if (!sortedActionDetails?.length) {
            return {};
        }
        return sortedActionDetails.reduce((highest, current) => {
            if (getMailStatusPriority(current.event) < getMailStatusPriority(highest.event)) {
                return current;
            }
            return highest;
        });
    }, [sortedActionDetails]);

    if (!sortedActionDetails?.length) {
        return null;
    }

    return (
        <div className={Style['email-status']}>
            <b>{t('ticket.moderation.attendee.history.emailStatus.text')} : </b>
            <Status
                color={getMailStatus(highestPriorityStatus.event)}
                text={t(
                    [`ticket.moderation.attendee.history.emailStatus.${formatMailStatus(highestPriorityStatus.event)}`, 'ticket.moderation.attendee.history.emailStatus.fallback'],
                    {
                        action: formatMailStatus(highestPriorityStatus.event)
                    }
                )}
            />
        </div>
    );
};
